import React from 'react';
import { AmhsLabel, StatusIndicator } from '../shared';
import { type AmhsEquipmentTint, type EquipmentStatus } from '../types';
import { PortShutter } from './PortShutter';
import { LoadPortAlignGrid } from './LoadPortAlignGrid';
import { getEquipmentBadgeTexture } from '../../../../utils/equipmentBadgeTexture';
import {
  getEquipmentHmiTexture,
  getStatusReadoutTexture,
  getWindowInteriorTexture,
} from '../../../../utils/equipmentCabinetTextures';
import {
  getProcessEquipmentDeck,
  getStandaloneLoadPortPoses,
  type EquipmentPortVisual,
  type Vec3,
} from './equipmentPortTransfer';
import { useRailElevation } from '../../railElevationContext';
import { FabAmhsLifter3D } from '../Lifter/FabAmhsLifter3D';
import { FOUP } from '../FOUP/FOUP';
import { resolveKitFoupScale } from '../types';

export type LoadPortProps = {
  width?: number;
  height?: number;
  depth?: number;
  foupPresent?: boolean;
  /** Along-dock fraction of the 2D port, so the tray sits under the drop node. */
  portFractions?: readonly number[];
  portVisual?: EquipmentPortVisual | null;
  flyingFoup?: { position: Vec3; scaleHint?: number } | null;
  isTransferring?: boolean;
  transferProgress01?: number;
  transferOperation?: 'PICKUP' | 'DROP';
  status?: EquipmentStatus;
  showLabel?: boolean;
  label?: string;
  opacity?: number;
  tint?: AmhsEquipmentTint;
};

const CAB = {
  navy: '#1A3050',
  navyDeep: '#12243C',
  beige: '#E6D9C4',
  beigeDoor: '#EDE3D2',
  top: '#F2F4F5',
  cyan: '#2FE6D4',
  amber: '#E8B45A',
  black: '#141414',
  ready: '#3DCC5A',
  busy: '#E8C44A',
  error: '#E24B4B',
  estop: '#D62828',
  estopRing: '#F0D020',
  dim: '#6B6560',
};

/** Standalone OHT-LOAD-PORT bay — same cabinet language as the process tool. */
export const LoadPort: React.FC<LoadPortProps> = ({
  width = 2,
  height = 2.5,
  depth = 1.5,
  foupPresent = false,
  portFractions,
  portVisual = null,
  flyingFoup = null,
  isTransferring = false,
  transferProgress01 = 0,
  transferOperation = 'PICKUP',
  status = 'IDLE',
  showLabel = false,
  label = 'LP',
  opacity = 1,
  tint: _tint,
}) => {
  void _tint;
  const dynamicRailY = useRailElevation();
  const TRACK_Y = Math.max(30.0, dynamicRailY ?? 32.0);
  const a = opacity < 1;
  const deck = getProcessEquipmentDeck(width, height, depth, 1, portFractions);
  const pose = getStandaloneLoadPortPoses(width, height, depth, portFractions)[0]!;
  const deckThickness = Math.max(0.03, height * 0.012);
  const shutterOpen = portVisual?.shutterOpen ?? 0;
  const foupInFlight = !!flyingFoup || portVisual?.foupMode === 'flying';
  const portInternalMotion =
    portVisual?.foupMode === 'flying' ||
    (portVisual?.transferState === 'loading' && foupInFlight);
  const lifterTransferring =
    (isTransferring && !foupInFlight && !portInternalMotion) ||
    ((portVisual?.transferState === 'loading' ||
      portVisual?.transferState === 'unloading') &&
      !foupInFlight &&
      portVisual?.foupMode !== 'flying');
  const lifterProgress = isTransferring ? transferProgress01 : 0;
  const lifterOp: 'PICKUP' | 'DROP' = isTransferring
    ? transferOperation
    : portVisual?.transferState === 'loading'
      ? 'DROP'
      : 'PICKUP';
  const showResting =
    portVisual?.foupMode === 'resting' && !foupInFlight && !lifterTransferring
      ? true
      : !portVisual && foupPresent && !foupInFlight && !lifterTransferring;
  const foupScale = resolveKitFoupScale(width, height, depth);
  const bodyH = height * 0.72;
  const bodyW = width * 0.9;
  const bodyD = depth * 0.62;
  const frontZ = -depth * 0.08 + bodyD * 0.5;
  const badge = React.useMemo(() => getEquipmentBadgeTexture(label), [label]);
  const badgeW = Math.min(width * 0.28, height * 0.07 * badge.aspect);
  const hmi = React.useMemo(() => getEquipmentHmiTexture(), []);
  const windowTex = React.useMemo(() => getWindowInteriorTexture(), []);
  const statusScreen = React.useMemo(() => getStatusReadoutTexture(status), [status]);
  const readyOn = status === 'IDLE' || status === 'RUNNING';
  const busyOn = status === 'RUNNING';
  const errorOn = status === 'ERROR' || status === 'WARNING';

  const shell = (color: string, metalness = 0.28, roughness = 0.42) => (
    <meshStandardMaterial
      color={color}
      metalness={metalness}
      roughness={roughness}
      transparent={a}
      opacity={opacity}
    />
  );
  const emissive = (color: string, intensity: number) => (
    <meshStandardMaterial
      color={color}
      emissive={color}
      emissiveIntensity={intensity}
      toneMapped={false}
      transparent={a}
      opacity={opacity}
    />
  );

  return (
    <group name="LoadPort">
      {([-1, 1] as const).flatMap((x) => ([-1, 1] as const).map((z) => [x, z] as const)).map(([x, z]) => (
        <mesh key={`foot-${x}-${z}`} position={[x * width * 0.36, height * 0.016, z * depth * 0.28]}>
          <cylinderGeometry args={[height * 0.02, height * 0.026, height * 0.032, 14]} />
          {shell('#1A1A1A', 0.45, 0.35)}
        </mesh>
      ))}

      <mesh position={[0, height * 0.04, -depth * 0.06]} receiveShadow>
        <boxGeometry args={[bodyW * 1.02, height * 0.08, bodyD * 1.02]} />
        {shell(CAB.navyDeep, 0.4, 0.38)}
      </mesh>
      <mesh position={[0, height * 0.12 + bodyH / 2, -depth * 0.08]} castShadow receiveShadow>
        <boxGeometry args={[bodyW, bodyH, bodyD]} />
        {shell(CAB.navy, 0.32, 0.48)}
      </mesh>
      <mesh position={[0, height * 0.94, -depth * 0.08]} castShadow>
        <boxGeometry args={[width * 0.98, height * 0.055, bodyD * 1.08]} />
        {shell(CAB.top, 0.18, 0.46)}
      </mesh>
      <mesh position={[0, height * 0.905, -depth * 0.08]}>
        <boxGeometry args={[width * 0.9, height * 0.01, bodyD * 1.04]} />
        {emissive(CAB.amber, 0.85)}
      </mesh>
      <mesh position={[0, height * 0.975, depth * 0.02]}>
        <boxGeometry args={[width * 0.18, height * 0.02, depth * 0.12]} />
        {shell(CAB.black, 0.4, 0.3)}
      </mesh>
      <mesh position={[0, height * 0.988, depth * 0.02]}>
        <boxGeometry args={[width * 0.11, height * 0.008, depth * 0.07]} />
        {emissive(CAB.cyan, 0.95)}
      </mesh>
      <mesh position={[width * 0.3, height * 1.05, frontZ + 0.03]}>
        <planeGeometry args={[badgeW, badgeW / badge.aspect]} />
        <meshBasicMaterial map={badge.texture} transparent opacity={opacity} toneMapped={false} />
      </mesh>

      <mesh position={[-width * 0.18, height * 0.62, frontZ + 0.012]} castShadow>
        <boxGeometry args={[width * 0.48, height * 0.4, 0.03]} />
        {shell(CAB.beigeDoor, 0.12, 0.55)}
      </mesh>
      <mesh position={[-width * 0.18, height * 0.64, frontZ + 0.03]}>
        <planeGeometry args={[width * 0.16, height * 0.28]} />
        <meshStandardMaterial
          map={windowTex.texture}
          transparent
          opacity={0.92 * opacity}
          metalness={0.15}
          roughness={0.18}
          emissive="#123A58"
          emissiveIntensity={0.35}
        />
      </mesh>
      <mesh position={[width * 0.22, height * 0.62, frontZ + 0.012]} castShadow>
        <boxGeometry args={[width * 0.38, height * 0.4, 0.03]} />
        {shell(CAB.beige, 0.12, 0.55)}
      </mesh>
      <mesh position={[width * 0.18, height * 0.66, frontZ + 0.03]}>
        <planeGeometry args={[width * 0.24, height * 0.16]} />
        <meshBasicMaterial map={hmi.texture} toneMapped={false} transparent opacity={opacity} />
      </mesh>
      <mesh position={[width * 0.34, height * 0.66, frontZ + 0.032]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[height * 0.022, height * 0.022, 0.012, 16]} />
        {emissive(CAB.estopRing, 0.45)}
      </mesh>
      <mesh position={[width * 0.34, height * 0.66, frontZ + 0.04]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[height * 0.015, height * 0.015, 0.014, 16]} />
        {emissive(CAB.estop, 0.55)}
      </mesh>

      <mesh position={[0, height * 0.38, frontZ + 0.012]}>
        <boxGeometry args={[bodyW * 0.88, height * 0.1, 0.022]} />
        {shell(CAB.beigeDoor, 0.12, 0.5)}
      </mesh>
      {(
        [
          [CAB.ready, readyOn, -0.12],
          [CAB.busy, busyOn, 0],
          [CAB.error, errorOn, 0.12],
        ] as const
      ).map(([color, on, x]) => (
        <mesh key={color} position={[x * width, height * 0.4, frontZ + 0.026]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[height * 0.012, height * 0.012, 0.01, 12]} />
          {emissive(on ? color : CAB.dim, on ? 1.05 : 0.06)}
        </mesh>
      ))}
      <mesh position={[width * 0.16, height * 0.38, frontZ + 0.026]}>
        <planeGeometry args={[width * 0.2, height * 0.07]} />
        <meshBasicMaterial map={statusScreen.texture} toneMapped={false} transparent opacity={opacity} />
      </mesh>

      <mesh position={[0, height * 0.24, frontZ + 0.01]} castShadow>
        <boxGeometry args={[bodyW * 0.9, height * 0.14, 0.03]} />
        {shell(CAB.navy, 0.35, 0.42)}
      </mesh>
      {Array.from({ length: 6 }, (_, i) => (
        <mesh key={`vent-${i}`} position={[0, height * (0.19 + i * 0.014), frontZ + 0.02]}>
          <boxGeometry args={[bodyW * 0.68, height * 0.006, 0.008]} />
          {shell(CAB.navyDeep, 0.25, 0.5)}
        </mesh>
      ))}
      <mesh position={[0, height * 0.3, frontZ + 0.02]}>
        <boxGeometry args={[bodyW * 0.4, height * 0.01, 0.01]} />
        {emissive(CAB.cyan, 0.9)}
      </mesh>

      {/* Same deck resolver as the process tool: the pad scales with the kit,
          sits under the port's own along-face fraction, and its plinth reaches
          the floor instead of stopping short of it. */}
      <LoadPortAlignGrid
        x={pose.loadPort[0]}
        width={deck.nestWidth}
        depth={deck.trayDepth}
        y={deck.deckY}
        z={deck.trayCenterZ}
        baseHeight={Math.max(deck.deckY, deckThickness * 4)}
        active={shutterOpen}
        opacity={opacity}
      />

      <group position={pose.shutter}>
        <PortShutter
          open={shutterOpen}
          width={pose.shutterSize[0]}
          height={pose.shutterSize[1]}
          depth={pose.shutterSize[2]}
          openTravel={pose.shutterOpenTravel}
          opacity={opacity}
          status={status}
        />
      </group>

      {/* Centralized AMHS Vertical Lifter connecting load port deck to overhead track */}
      <FabAmhsLifter3D
        id={`LoadPortLifter-${label}`}
        position={[pose.loadPort[0], 0, pose.loadPort[2]]}
        bottomY={deck.deckY}
        topTrackY={TRACK_Y}
        width={Math.max(deck.nestWidth * 1.15, 2.4)}
        depth={Math.max(deck.trayDepth * 1.15, 2.4)}
        isTransferring={lifterTransferring}
        progress01={lifterProgress}
        transferOperation={lifterOp}
        foupPresent={showResting}
        active={showResting || lifterTransferring}
        status={status}
        opacity={opacity}
        showTrackClamp={true}
        showFoup={!foupInFlight}
      />

      {flyingFoup?.position ? (
        <FOUP
          position={flyingFoup.position}
          scale={flyingFoup.scaleHint ?? foupScale}
          opacity={opacity}
        />
      ) : portVisual?.foupMode === 'flying' && portVisual.foupPosition ? (
        <FOUP
          position={portVisual.foupPosition}
          scale={foupScale}
          opacity={opacity}
        />
      ) : null}

      <StatusIndicator status={status} position={[width * 0.38, height * 0.9, depth * 0.18]} size={0.055} />
      <AmhsLabel text={label} y={height + 0.48} visible={showLabel} />
    </group>
  );
};

export default LoadPort;
